// src/pages/inbox/index.tsx
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SidebarLayout } from "@/layouts/SidebarLayout";
import { EmailSidebar } from "@/components/email/EmailSidebar";
import { EmailTemplateList } from "@/components/email/templates/EmailTemplateList";
import { ScheduledEmailsCalendar } from "@/components/email/ScheduledEmailsCalendar";
import { QueueManagementPanel } from "@/components/email/queue/QueueManagementPanel";
import { QueueHealthCard } from "@/components/email/safety/QueueHealthCard";
import { SafetyAlertsPanel } from "@/components/email/safety/SafetyAlertsPanel";
import { SafetyControlsManager } from "@/components/email/safety/SafetyControlsManager";
import { CampaignManagementPage } from "@/components/email/campaigns/CampaignManagementPage";
import { SentEmailsList } from "@/components/email/SentEmailsList";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { Button } from "@/components/ui/button";
import { ShieldAlert } from "lucide-react";

const KNOWN_TABS = ["campaigns", "activity", "sent", "templates", "admin"];

export function EmailPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || "campaigns");
  const { currentWorkspace } = useWorkspaces();
  const isAdmin = currentWorkspace?.role === 'owner' || currentWorkspace?.role === 'admin';

  // Update URL when tab changes
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  // Update active tab when URL changes
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  // Redirect stale/unknown tabs (e.g. old ?tab=scheduled bookmarks) to Campaigns.
  // The admin tab stays "known" so non-admins get an access message, not a silent redirect.
  useEffect(() => {
    if (!KNOWN_TABS.includes(activeTab)) {
      setSearchParams({ tab: "campaigns" }, { replace: true });
    }
  }, [activeTab, setSearchParams]);

  return (
    <SidebarLayout 
      sidebar={
        <EmailSidebar 
          activePage={activeTab} 
          onPageChange={handleTabChange}
        />
      }
    >
      <div className="h-full flex flex-col">
        <div className="flex-1 p-8">
          {activeTab === "templates" ? (
            <EmailTemplateList />
          ) : activeTab === "activity" ? (
            <ScheduledEmailsCalendar />
          ) : activeTab === "campaigns" ? (
            <CampaignManagementPage />
          ) : activeTab === "sent" ? (
            <SentEmailsList />
          ) : activeTab === "admin" ? (
            isAdmin ? (
              <div className="space-y-6">
                <QueueHealthCard />
                <QueueManagementPanel />
                <SafetyAlertsPanel />
                <SafetyControlsManager />
              </div>
            ) : (
              <AdminAccessDenied onBack={() => handleTabChange("campaigns")} />
            )
          ) : null}
        </div>
      </div>
    </SidebarLayout>
  );
}

function AdminAccessDenied({ onBack }: { onBack: () => void }) {
  return (
    <div className="text-center py-12">
      <ShieldAlert className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
      <h3 className="text-lg font-medium">Admin access required</h3>
      <p className="text-sm text-muted-foreground mt-1 mb-4">
        Queue and safety controls are only available to workspace owners and admins.
      </p>
      <Button variant="outline" onClick={onBack}>
        Go to Campaigns
      </Button>
    </div>
  );
}
